import { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Box, Button } from '@mui/material'
import colors from 'common/colors'
import ManageUsers from 'components/farm-settings/manage-users'
import { showModal } from 'actions/app'
import SearchField from 'components/search-field'
import { DataGrid } from '@mui/x-data-grid'
import { fetchInvitedUsers, removeAccessibleAccount } from 'api/tenant'

export default function InvitedUsers({ users = [] }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState('')

  const columns = [
    {
      field: 'email',
      headerName: t('email'),
      minWidth: 500,
      flex: 1,
    },
    {
      field: 'role',
      headerName: t('role'),
      minWidth: 400,
      flex: 1,
    },
    {
      field: 'action',
      headerName: t('action'),
      sortable: false,
      minWidth: 100,
      headerAlign: 'center',
      flex: 1,
      renderCell: (params) => {
        return (
          <Button
            variant="contained"
            color="primary"
            onClick={() => onRemove(params.row)}
          >
            {t('delete')}
          </Button>
        )
      },
    },
  ]

  useEffect(() => {
    dispatch(fetchInvitedUsers())
  }, [])

  const onInviteClick = useCallback(() => {
    dispatch(showModal(<ManageUsers />, { title: t('invite_agpro') }))
  }, [])

  const onRemove = useCallback((user) => {
    const { tenantId, userId } = user
    dispatch(removeAccessibleAccount(tenantId, userId))
  }, [])

  const onSearch = useCallback(
    (value) => {
      setSearchValue(value)
    },
    [searchValue],
  )

  const filterUsers = (searchValue, user) => {
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase()

      return (
        user.email?.toLowerCase().indexOf(lowerSearchValue) > -1 ||
        user.role?.toLowerCase().indexOf(lowerSearchValue) > -1
      )
    }

    return true
  }

  function CustomNoRowsOverlayUsers() {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {t('no_users_found')}
      </div>
    )
  }

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1>{t('agpro_users')}</h1>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <SearchField
            sx={{ width: '300px' }}
            name={searchValue}
            placeholder={t('search')}
            onSearch={onSearch}
          />
        </Box>
      </Box>
      <div
        style={{
          flexGrow: 1,
          height: '100%',
          width: '100%',
          maxHeight: '800px',
          marginTop: '2rem',
        }}
      >
        <DataGrid
          autoHeight
          slots={{
            noRowsOverlay: CustomNoRowsOverlayUsers,
          }}
          rows={users
            .map((u) => ({ ...u, id: u.email }))
            .filter((u) => filterUsers(searchValue, u))}
          columns={columns}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: colors.greyBackground,
              textTransform: 'uppercase',
            },
          }}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 20 } },
          }}
          pageSizeOptions={[10, 20, 50]}
        />
        <Button
          onClick={onInviteClick}
          variant="contained"
          color="primary"
          sx={{ marginTop: '1em', width: 'fit-content' }}
        >
          {t('invite')}
        </Button>
      </div>
    </Box>
  )
}
